import { intersection } from 'lodash-es';
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

import { SET_PREVIOUS_URL } from '@/store/action-types';
import { RootActions } from '@/store/types/action-types';
import { parseJwt } from '@/bundles/App/helpers/parseJWT';
import { RoleNameEnum } from '@/bundles/Roles/types';

const handlingAccountIdRoutes = (to, store) => {
  if (isNaN(to.params.accountId)) {
    const accountId = store?.state?.activeAccount?.id;

    return `/${accountId}${to.fullPath}`;
  }

  return '';
};

export const redirectHandler = (to, next, store, isRedirectTo404 = false) => {
  const route = handlingAccountIdRoutes(to, store);

  if (route) {
    next(route);
    return;
  }

  // the not-found page sits under the account, so it takes the account from the URL
  isRedirectTo404 ? next({ name: '404', params: { accountId: to.params.accountId } }) : next();
};

export const requiresTokenAbsenceGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  // a sign-out leaves the page while the token is still set; it clears the session afterwards
  if (store?.state?.idToken && !store?.state?.isLoggingOut) {
    const accountId = store?.state?.activeAccount?.id ?? store?.state?.user?.active_account?.id;

    if (accountId) {
      next({ name: 'dashboard', params: { accountId } });
      return false;
    }

    // The token lives in a cookie, the active account in localStorage: a fresh
    // origin (another localhost port, cleared site data) has the first without
    // the second. Router 4 throws on the missing :accountId (Router 3 only
    // warned), so load the user's account before redirecting. If the token no
    // longer works, stay on the welcome page to sign in again.
    store.dispatch(RootActions.FETCH_CURRENT_USER)
      .then(() => next({ name: 'dashboard', params: { accountId: store.state.activeAccount.id } }))
      .catch(() => next());
    return false;
  }

  return true;
};

export const requiresTokenOrTemporaryTokenGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  if (!store?.state?.idToken && !store?.state?.temporaryAuthToken) {
    next('/signin');
    return false;
  }

  return true;
};

export const requiresTokenGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  if (!store?.state?.idToken) {
    // History-mode router: use the relative target path (incl. query) so the
    // post-login `router.push(prevUrl)` resolves to a real route instead of
    // falling through to /404. `to.fullPath` = e.g. /oauth/authorize?client_id=…
    // SignInCard opens this page again once the user has signed in
    store.commit(SET_PREVIOUS_URL, to.fullPath || '/dashboard');
    next('/signin');

    return false;
  }

  store.commit(SET_PREVIOUS_URL, null);
  return true;
};

export const requiresRedirectGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  redirectHandler(to, next, store);
  return false;
};

export const requiresAdminTokenGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  if (!store.state.adminToken || new Date().getTime() > store.state.adminTokenExpiresTime || to.query.showLogin) {
    next(`${to.path}?showLogin=true`);
    return false;
  }

  return true;
};

export const requiresSuperAdminRoleGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  const roles = [RoleNameEnum.SUPER_ADMIN];
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];

  if (intersection(roles, userRoles).length) {
    return true;
  }

  next('/access');
  return false;
};

export const requiresFinancialAdminRoleGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  const roles = [RoleNameEnum.SUPER_ADMIN, RoleNameEnum.FINANCIAL_ADMIN];
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];

  if (intersection(roles, userRoles).length) {
    return true;
  }

  next('/access');
  return false;
};

export const requiresFinancialOrMarketingAdminRoleGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  const roles = [RoleNameEnum.SUPER_ADMIN, RoleNameEnum.FINANCIAL_ADMIN, RoleNameEnum.MARKETING_ADMIN];
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];

  if (intersection(roles, userRoles).length) {
    return true;
  }

  next('/access');
  return false;
};

export const requiresAdminRoleGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  const roles = [RoleNameEnum.ADMIN, RoleNameEnum.SUPER_ADMIN];
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];

  if (intersection(roles, userRoles).length) {
    return true;
  }

  next('/access');
  return false;
};

export const requiresFinancialAdminOrAdminRoleGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  const roles = [RoleNameEnum.SUPER_ADMIN, RoleNameEnum.FINANCIAL_ADMIN, RoleNameEnum.ADMIN];
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];

  if (intersection(roles, userRoles).length) {
    return true;
  }

  next('/access');
  return false;
};

export const requireRegularUser = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];
  if (!userRoles.includes('client') || (userRoles.includes('client') && to.meta?.allowClientAccess) || to.path.includes('access')) {
    return true;
  }
  next('/access');
  return false;
};

export const requiresPasswordChangeGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => {
  const isPasswordTemporary = store?.state?.isPasswordTemporary;
  const accountId = store?.state?.activeAccount?.id;

  if (isPasswordTemporary && to.name !== 'user-security') {
    next({ name: 'user-security', params: { accountId } });
    return false;
  }

  return true;
};

type RouteGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any) => boolean;

interface IRouteGuardEntry {
  applies: (to: RouteLocationNormalized) => boolean;
  guard: RouteGuard;
}

// Every guard returns true to let the navigation through without touching `next`,
// or false once it has called (or will call) `next` itself. Order matters: the
// first guard that redirects ends the chain.
const routeGuards: IRouteGuardEntry[] = [
  { applies: () => true, guard: requireRegularUser },
  { applies: to => !!to.meta?.requiresTokenOrTemporaryToken, guard: requiresTokenOrTemporaryTokenGuard },
  { applies: to => !!to.meta?.requiresTokenAbsence, guard: requiresTokenAbsenceGuard },
  { applies: to => to.matched.some(record => record.meta?.requiresToken), guard: requiresTokenGuard },
  { applies: to => !!to.meta?.requiresRedirect, guard: requiresRedirectGuard },
  { applies: to => !!to.meta?.requiresAdminToken, guard: requiresAdminTokenGuard },
  { applies: to => !!to.meta?.requiresFinancialAdminRole, guard: requiresFinancialAdminRoleGuard },
  { applies: to => !!to.meta?.requiresFinancialOrMarketingAdminRole, guard: requiresFinancialOrMarketingAdminRoleGuard },
  { applies: to => !!to.meta?.requiresSuperAdminRole, guard: requiresSuperAdminRoleGuard },
  { applies: to => !!to.meta?.requiresFinancialAdminOrAdminRole, guard: requiresFinancialAdminOrAdminRoleGuard },
  { applies: to => !!to.meta?.requiresAdminRole, guard: requiresAdminRoleGuard },
  { applies: () => true, guard: requiresPasswordChangeGuard },
];

/**
 * Runs the guards that apply to `to` in order and stops at the first one that
 * takes over the navigation, so `next` is called at most once. Returns true when
 * every guard let the navigation through and the caller should call `next()`.
 */
export const runRouteGuards = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, store: any): boolean => {
  return routeGuards.every(({ applies, guard }) => !applies(to) || guard(to, from, next, store));
};

// Pending GET requests are cancelled when the user leaves a page, so a slow
// list response never lands on the next screen. A navigation that only changes
// the query of the route the user is already on (`$router.replace({ query })`
// from a filter bar, or a page normalising its own query on mount) keeps the
// page and its cards mounted, so their requests still have a consumer. Vue 3
// flushes a child's watchers before the parent's `mounted`, so the pipeline
// dashboard's cards had already sent their requests when the page's query
// sync ran and this hook aborted them -- every card stayed a skeleton.
export const shouldCancelPendingRequests = (to: RouteLocationNormalized, from: RouteLocationNormalized): boolean => to.path !== from.path;
